/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import { useCallback, useState } from 'react';
import { useMachine } from '@xstate/react';
import { containerListMachine } from './containerListMachine';
import type { ContainerQuery, ContainerSortField } from './containerQuarantine.types';

/** @param initialQuery the sort and page to start from, such as the ones the container detail links back with */
export function useContainerList(initialQuery?: ContainerQuery) {
  // Read once: the machine owns the query after the first render.
  const [machine] = useState(() =>
    initialQuery
      ? containerListMachine.withContext({ ...containerListMachine.context, query: initialQuery })
      : containerListMachine
  );
  const [state, send] = useMachine(machine);
  const { query, data, error } = state.context;

  return {
    hasData: data !== null,
    items: data?.results ?? [],
    total: data?.total ?? 0,
    query,
    loading: state.matches('loading'),
    failed: state.matches('failed'),
    unavailable: state.matches('unavailable'),
    error,
    setSort: useCallback(
      (sortBy: ContainerSortField, asc: boolean) => send({ type: 'SET_SORT', sortBy, asc }),
      [send]
    ),
    setPage: useCallback((page: number) => send({ type: 'SET_PAGE', page }), [send]),
    setPageSize: useCallback((pageSize: number) => send({ type: 'SET_PAGE_SIZE', pageSize }), [send]),
    retry: useCallback(() => send({ type: 'RETRY' }), [send]),
  };
}
